import { useCallback, useEffect, useState } from "react";

import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { ulid } from "ulid";

import Grid from "@mui/material/Grid";
import {
  DataGrid,
  gridRowSelectionIdsSelector,
  useGridApiRef,
} from "@mui/x-data-grid";

import { Button } from "@mui/material";

import { useOutletApi } from "@/features/authentication";
import { StatusEnum } from "@/features/purchase-orders";
import { formatDate, formatNumber } from "@/features/shared";
import {
  CustomAsyncAutocomplete,
  CustomSelect,
  ProductVariantNameCell,
} from "@/features/ui";

export const SelectPurchaseOrderProductVariantSection = () => {
  const { control } = useFormContext();
  const apiRef = useGridApiRef();
  const [purchaseOrder, setPurchaseOrder] = useState(null);
  const [purchaseOrderType, setPurchaseOrderType] = useState("standard");
  const [productVariants, setProductVariants] = useState([]);
  const [loadingProductVariants, setLoadingProductVariants] = useState(false);
  const [
    selectedPurchaseOrderProductVariants,
    setSelectedPurchaseOrderProductVariants,
  ] = useState([]);

  const { append, update } = useFieldArray({
    control,
    name: "goodsReceiptProductVariants",
  });

  const [seller, buyer, warehouse, goodsReceiptProductVariants] = useWatch({
    control,
    name: ["seller", "buyer", "warehouse", "goodsReceiptProductVariants"],
    exact: true,
  });

  const outletApi = useOutletApi(buyer?.id);

  const disableBrowsePurchaseOrder = !seller || !warehouse;

  const fetchUnfinishedPurchaseOrders = useCallback(
    async (searchValue, { page, size } = {}) => {
      if (!seller || !buyer || !warehouse) return [];

      const unfinishedStatuses = [
        StatusEnum.PENDING,
        StatusEnum.PARTIALLY_PROCESSED,
      ].join(",");

      const {
        data: { data },
      } = await outletApi.get(`purchase-orders/${purchaseOrderType}`, {
        params: {
          page,
          size,
          status: unfinishedStatuses,
          sellerableId: seller?.id,
          warehouseId: warehouse?.id,
          referenceNumber: searchValue || null,
        },
      });
      return data;
    },
    [buyer, outletApi, purchaseOrderType, seller, warehouse]
  );

  const fetchPurchaseOrderProductVariants = useCallback(
    async (referenceNumber) => {
      if (!referenceNumber || !buyer) return;

      setLoadingProductVariants(true);

      try {
        const {
          data: { data },
        } = await outletApi.get(
          `purchase-orders/${purchaseOrderType}/${referenceNumber}/purchase-order-product-variants`,
          {
            params: {
              excludeFullyProcessed: true,
            },
          }
        );

        // 🔥 Adjust remaining quantity based on GRPV
        const adjustedProductVariants = data
          .map((purchaseOrderProductVariant) => {
            // Calculate total quantity for this variant across all GRPV entries
            const totalGoodsReceiptQuantity = goodsReceiptProductVariants
              .filter(
                (goodReceiptProductVariant) =>
                  goodReceiptProductVariant.purchaseOrderProductVariantId ===
                  purchaseOrderProductVariant.id
              )
              .reduce((total, grpv) => total + grpv.quantity, 0);

            return {
              ...purchaseOrderProductVariant,
              adjustedRemainingQuantity:
                purchaseOrderProductVariant.quantity -
                purchaseOrderProductVariant.receivedQuantity -
                totalGoodsReceiptQuantity, // Adjust based on total GRPV quantity
            };
          })
          .filter((variant) => variant.adjustedRemainingQuantity > 0); // 🚀 Keep only variants with available stock

        setProductVariants(adjustedProductVariants);
      } catch (error) {
        console.log(error);
      } finally {
        setLoadingProductVariants(false);
      }
    },
    [buyer, goodsReceiptProductVariants, outletApi, purchaseOrderType]
  );

  useEffect(() => {
    if (purchaseOrder) {
      fetchPurchaseOrderProductVariants(purchaseOrder.referenceNumber);
    } else {
      setProductVariants([]);
    }
    setSelectedPurchaseOrderProductVariants([]); // Reset selection when purchase order changes
  }, [fetchPurchaseOrderProductVariants, purchaseOrder]);

  const columns = [
    {
      field: "name",
      headerName: "Nama",
      flex: 5,
      renderCell: ({ row: { name, sku } }) => (
        <ProductVariantNameCell name={name} sku={sku} />
      ),
    },
    {
      field: "adjustedRemainingQuantity",
      headerName: "Qty",
      type: "number",
      valueFormatter: (value) => formatNumber(value),
    },
    {
      field: "unitOfMeasurement",
      headerName: "Unit",
      valueGetter: (_, row) => row.product.unitOfMeasurement.name,
    },
    {
      field: "note",
      headerName: "Catatan",
      flex: 3,
      valueGetter: (value) => value || "-",
    },
  ];

  const submitSelection = () => {
    // Reset the state after submission
    setPurchaseOrder(null);
    setProductVariants([]);

    selectedPurchaseOrderProductVariants.forEach(
      ({
        id: purchaseOrderProductVariantId,
        name,
        sku,
        note,
        adjustedRemainingQuantity,
        product: {
          unitOfMeasurement: { name: unitOfMeasurement },
        },
      }) => {
        // 🔍 Check if the purchase order product variant already exists in GRPV
        const existingGoodsReceiptProductVariantIndex =
          goodsReceiptProductVariants.findIndex(
            (goodsReceiptProductVariant) =>
              goodsReceiptProductVariant.purchaseOrderProductVariantId ===
              purchaseOrderProductVariantId
          );

        if (existingGoodsReceiptProductVariantIndex !== -1) {
          // ✅ Update existing goods receipt product variant quantity
          update(existingGoodsReceiptProductVariantIndex, {
            ...goodsReceiptProductVariants[
              existingGoodsReceiptProductVariantIndex
            ],
            quantity:
              goodsReceiptProductVariants[
                existingGoodsReceiptProductVariantIndex
              ].quantity + adjustedRemainingQuantity, // Merge quantities
          });
        } else {
          // 🔥 Append as new record if not found
          append({
            id: ulid(),
            purchaseOrderProductVariantId,
            name,
            sku,
            quantity: adjustedRemainingQuantity,
            maxQuantity: adjustedRemainingQuantity,
            unitOfMeasurement,
            // Carried from the order line; still editable in the grid.
            note: note ?? null,
            invoicedQuantity: 0,
            returnedQuantity: 0,
          });
        }
      }
    );
  };

  return (
    <>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, sm: 4, lg: 3 }}>
          <CustomSelect
            id="purchase-order-type"
            options={[
              { value: "standard", label: "PO Bulk" },
              { value: "lab", label: "PO Lab." },
            ]}
            value={purchaseOrderType}
            onChange={(e) => setPurchaseOrderType(e.target.value)}
            sx={{ minWidth: 120 }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 8, lg: 9 }}>
          <CustomAsyncAutocomplete
            value={purchaseOrder}
            onChange={setPurchaseOrder}
            disabled={disableBrowsePurchaseOrder}
            fetchOptions={fetchUnfinishedPurchaseOrders}
            getOptionLabel={(option) => option.referenceNumber}
            isOptionEqualToValue={(option, selectedOption) =>
              option.id === selectedOption.id
            }
            renderOption={(props, option) => {
              const { id, referenceNumber, transactionDate } = option;
              return (
                <li {...props} key={id}>
                  <div>
                    <div>{referenceNumber}</div>
                    <div style={{ fontSize: "0.75rem" }}>
                      {formatDate(transactionDate)}
                    </div>
                  </div>
                </li>
              );
            }}
            TextFieldProps={{
              helperText: disableBrowsePurchaseOrder
                ? "Pilih Pemasok dan Gudang terlebih dahulu"
                : null,
              InputProps: {
                placeholder: "Cari nomor referensi pesanan",
              },
            }}
          />
        </Grid>
      </Grid>

      <DataGrid
        getRowHeight={() => "auto"}
        apiRef={apiRef}
        columns={columns}
        rows={productVariants}
        loading={loadingProductVariants}
        checkboxSelection
        onRowSelectionModelChange={() => {
          const selectedModels = Array.from(
            gridRowSelectionIdsSelector(apiRef.current.state).values()
          );
          setSelectedPurchaseOrderProductVariants(selectedModels);
        }}
      />
      <Button
        fullWidth
        variant="contained"
        disabled={selectedPurchaseOrderProductVariants.length === 0}
        sx={{ mt: 1 }}
        onClick={submitSelection}
      >
        Tambah
      </Button>
    </>
  );
};
